import 'reflect-metadata';
import camelCase from 'lodash/camelCase';
import flatMap from 'lodash/flatMap';
import isArray from 'lodash/isArray';
import isEmpty from 'lodash/isEmpty';
import isPlainObject from 'lodash/isPlainObject';
import isString from 'lodash/isString';
import map from 'lodash/map';
import mapKeys from 'lodash/mapKeys';
import mapValues from 'lodash/mapValues';
import pick from 'lodash/pick';
import snakeCase from 'lodash/snakeCase';
import { LOG_TYPE } from '../const/logger.const';
import { logger } from '../logger/logger.class';
const getSubset = (keys, response, methodName) => {
    const message = `API Response of ${methodName} was hand-picked and contains only the following properties: ${keys}`;
    logger.message(message, LOG_TYPE.warning);
    return pick(response, keys);
};
/**
 * Performs deep object's key modification
 * @param collection {object} colloction to be modified
 * @param iterator {function}
 * @returns {Record<string, any>} - initial object with keys modified by iterator
 */
export function mapKeysDeep(collection, iterator) {
    if (isArray(collection)) {
        return map(collection, (item) => {
            return mapKeysDeep(item, iterator);
        });
    }
    if (!isPlainObject(collection)) {
        return collection;
    }
    const result = mapKeys(collection, iterator);
    return mapValues(result, (value) => {
        return mapKeysDeep(value, iterator);
    });
}
/**
 * Deep transformation of object's keys into snake_case
 * @param param {object}
 * @param methodName {string}
 * @returns {Record<string, any>}
 */
export function snakeCaseKeys(param, methodName) {
    const snakeCaseObject = mapKeysDeep(param, (_value, key) => {
        return snakeCase(key);
    });
    const message = `Payload keys for ${methodName} were transformed into snake_case`;
    logger.message(message, LOG_TYPE.info);
    return snakeCaseObject;
}
/**
 * Deep transformation of object's keys into camelCase
 * @param param {object}
 * @param methodName {string}
 * @returns {Record<string, any>}
 */
export function camelCaseKeys(param, methodName) {
    const camelCasedObject = mapKeysDeep(param, (_value, key) => {
        return camelCase(key);
    });
    const message = `Payload keys for ${methodName} were transformed into camelCase`;
    logger.message(message, LOG_TYPE.info);
    return camelCasedObject;
}
/**
 * Picks desired keys from API payload
 * @param param {string[]}
 * @param methodName {string}
 * @returns {Record<string, any>}
 */
export function pickKeys(response, args, methodName) {
    if (isString(args)) {
        return response;
    }
    const keys = flatMap(args.filter(isArray));
    return isEmpty(keys) ? response : getSubset(keys, response, methodName);
}
const isValidItem = (item, whitelist) => !!(item || (whitelist === null || whitelist === void 0 ? void 0 : whitelist.includes(item)));
/**
 * Filters falsy items from array
 * @param param {T[]}
 * @returns {T[]}
 */
export const rejectFalsyItems = (param, options) => param.filter(item => isValidItem(item, options === null || options === void 0 ? void 0 : options.whitelist));
/**
 * Filters falsy properties from object
 * @param param {Dictionary<T>}
 * @returns {Dictionary<T>}
 */
export const rejectFalsyProperties = (param, options) => {
    const modifiedParam = {};
    Object.keys(param)
        .filter(property => isValidItem(param[property], options === null || options === void 0 ? void 0 : options.whitelist))
        .forEach(truthyProperty => {
        modifiedParam[truthyProperty] = param[truthyProperty];
    });
    return modifiedParam;
};
// Export for unit test
export const requiredMetadataKey = Symbol('required');
/**
 * Indicates that a parameter is required via reflect-metadata
 * @param target {Dictionary<unknown>}
 * @param propertyKey {string | symbol}
 * @param parameterIndex {number}
 */
export const markParamaterRequired = (target, propertyKey, parameterIndex) => {
    const requiredParameters = Reflect.getMetadata(requiredMetadataKey, target, propertyKey) || [];
    requiredParameters.push(parameterIndex);
    Reflect.defineMetadata(requiredMetadataKey, requiredParameters, target, propertyKey);
};
/**
 * Gets indexes of required parameters that are not defined
 * @param target {Dictionary<unknown>}
 * @param propertyKey {string | symbol}
 * @param args {unknown[]}
 * @returns {number[]}
 */
export const getUndefinedParametersIndexes = (target, propertyKey, args) => {
    const requiredParameters = Reflect.getMetadata(requiredMetadataKey, target, propertyKey) || [];
    return requiredParameters.filter(parameterIndex => args[parameterIndex] === undefined);
};
/**
 * Logs class name when an instance of itself is initialized
 *
 * @param constructor
*/
export const logInitializedClass = (constructor) => {
    return class extends constructor {
        constructor(...args) {
            super(...args);
            logger.message(`New instance of class ${constructor.name} initialized`, LOG_TYPE.info);
            logger.message('Initialized at:', LOG_TYPE.info);
            // eslint-disable-next-line no-console
            console.trace();
        }
        ;
    };
};
